'use client';

import { Chevron } from '@/assets/Chevron';
import { buildBlogListQueryString, TBlogSortOption } from '@/components/blog-list/blogListParams';
import clsx from 'clsx';
import { ChangeEvent, startTransition, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

interface IBlogFilterControlsProps {
  selectedTags: string[];
  sortBy: TBlogSortOption;
  tagOptions: string[];
}

const SORT_OPTIONS: { label: string; value: TBlogSortOption }[] = [
  { label: 'Name A-Z', value: 'name-asc' },
  { label: 'Name Z-A', value: 'name-desc' },
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
];

export default function BlogFilterControls(props: IBlogFilterControlsProps) {
  const { selectedTags, sortBy, tagOptions } = props;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [draftSelectedTags, setDraftSelectedTags] = useState<string[]>(selectedTags);

  const filteredTags = useMemo(
    () => tagOptions.filter((tag) => tag.toLowerCase().includes(searchValue.trim().toLowerCase())),
    [searchValue, tagOptions]
  );
  const selectedTagSummary = selectedTags.length ? selectedTags.join(', ') : 'All tags';
  const hasDraftChanges =
    draftSelectedTags.length !== selectedTags.length || draftSelectedTags.some((tag) => !selectedTags.includes(tag));

  function pushParams(nextQueryString: string) {
    const nextUrl = nextQueryString ? `${pathname}?${nextQueryString}` : pathname;

    startTransition(() => {
      router.push(nextUrl, { scroll: false });
    });
  }

  function updateSelectedTags(nextSelectedTags: string[]) {
    pushParams(
      buildBlogListQueryString(new URLSearchParams(searchParams.toString()), {
        selectedTags: nextSelectedTags,
        page: 1,
      })
    );
  }

  function openDropdown() {
    setDraftSelectedTags(selectedTags);
    setSearchValue('');
    setIsOpen(true);
  }

  function closeDropdown() {
    setDraftSelectedTags(selectedTags);
    setSearchValue('');
    setIsOpen(false);
  }

  function applyFilters() {
    updateSelectedTags(draftSelectedTags);
    setSearchValue('');
    setIsOpen(false);
  }

  function handleToggleDropdown() {
    if (isOpen) {
      closeDropdown();
      return;
    }

    openDropdown();
  }

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchValue(event.target.value);
  }

  function handleClearDraftTags() {
    setDraftSelectedTags([]);
  }

  function handleToggleDraftTag(tag: string) {
    return () => {
      setDraftSelectedTags((currentTags) => {
        if (currentTags.includes(tag)) {
          return currentTags.filter((selectedTag) => selectedTag !== tag);
        }

        return [...currentTags, tag];
      });
    };
  }

  function handleSortChange(event: ChangeEvent<HTMLSelectElement>) {
    pushParams(
      buildBlogListQueryString(new URLSearchParams(searchParams.toString()), {
        sortBy: event.target.value as TBlogSortOption,
        page: 1,
      })
    );
  }

  return (
    <div className='mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-end'>
      <div className='flex flex-col gap-2 sm:w-full sm:max-w-sm'>
        <span className='text-primary-white text-xs font-semibold uppercase'>Filter by tag</span>
        <div className='relative w-full sm:max-w-sm'>
          <button
            aria-expanded={isOpen}
            aria-haspopup='listbox'
            aria-label='Filter by tag'
            className='border-primary-aqua bg-primary-black text-primary-white flex min-h-10 w-full items-center justify-between gap-3 rounded-full border px-4 py-2 text-left text-sm'
            type='button'
            onClick={handleToggleDropdown}
          >
            <span className='min-w-0 flex-1 truncate'>{selectedTagSummary}</span>
            <Chevron
              className={clsx('h-3 w-3 rotate-90 transition-transform duration-300', isOpen && 'rotate-[270deg]')}
              height={12}
              width={12}
            />
          </button>

          {isOpen && (
            <div className='bg-primary-white text-primary-black border-primary-darkAqua absolute right-0 left-0 z-20 mt-2 rounded-lg border p-2 shadow-lg'>
              <input
                aria-label='Search tags'
                className='text-primary-black border-primary-darkAqua mb-2 w-full rounded-md border px-3 py-2 outline-none'
                placeholder='Search'
                value={searchValue}
                onChange={handleSearchChange}
              />

              <div className='max-h-72 overflow-y-auto'>
                <label className='text-primary-black hover:bg-primary-lightGrey flex cursor-pointer items-center gap-3 rounded-md px-2 py-2'>
                  <input checked={!draftSelectedTags.length} type='checkbox' onChange={handleClearDraftTags} />
                  <span>All</span>
                </label>
                {filteredTags.map((tag) => (
                  <label
                    className='text-primary-black hover:bg-primary-lightGrey flex cursor-pointer items-center gap-3 rounded-md px-2 py-2'
                    key={tag}
                  >
                    <input
                      checked={draftSelectedTags.includes(tag)}
                      type='checkbox'
                      onChange={handleToggleDraftTag(tag)}
                    />
                    <span>{tag}</span>
                  </label>
                ))}
              </div>

              <div className='border-primary-darkAqua mt-3 flex justify-end gap-2 border-t pt-3'>
                <button
                  className='text-primary-black border-primary-darkAqua cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold'
                  type='button'
                  onClick={closeDropdown}
                >
                  Cancel
                </button>
                <button
                  className='bg-primary-darkAqua text-primary-white border-primary-darkAqua rounded-full border px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50'
                  disabled={!hasDraftChanges}
                  type='button'
                  onClick={applyFilters}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <label className='flex flex-col gap-2 sm:w-48 sm:flex-none'>
        <span className='text-primary-white text-xs font-semibold uppercase'>Sort by</span>
        <select
          aria-label='Sort by'
          className='border-primary-aqua bg-primary-black text-primary-white min-h-10 w-full rounded-full border px-4 py-2 text-sm'
          onChange={handleSortChange}
          value={sortBy}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
